import {Container} from "pixi.js";
import {FrmVar} from "../../drawfrm/FrmVar";
import {inches} from "../../const";
import type {ElevationApp} from "../../ElevationApp";
import {LinearDimension} from "./LinearDimension";

// anything that owns an editable width/height and wants an inside dimension drawn
// for it. Implemented by FrameBlockItem (leaf cells) and FrameBlockExt
// (rows/columns with an explicit size).
export interface InnerDimensionTarget {
    width: FrmVar;
    height: FrmVar;

    // a display container positioned at the target's top-left (scene coords); the
    // dimension lines and labels attach here and so move with the target.
    dimensionContainer: Container;

    // the owning view; only its `elev` (for the draw app) is read here, so it's
    // typed to that minimal shape to allow non-FrameGridView owners (e.g. an inert
    // stick hosted by the Locations scene).
    view: {elev: ElevationApp};

    // re-run the grid layout after this target's size is edited.
    relayout(): void;

    // called once an edit has committed, before the relayout, with the axis edited
    // and how far the value moved. Lets a target reallocate the change before the
    // layout is re-solved (a mullion pushes its thickness change into the cell
    // beside it). Returning false refuses the edit, and the value goes back. Optional —
    // a target that just resizes itself omits it.
    onDimensionEdit?(axis: "width" | "height", delta: number): boolean | void;
}

// how far the dimension line is inset from the item's edge, in scene units. the
// horizontal (width) dimension hugs the top by this amount; the vertical
// (height) dimension hugs the left.
const DIM_INSET = 4 * inches;

// renders the inside (overall) dimensions of a single FrameBlockItem: a width
// dimension when the target owns an editable width and/or a height dimension when
// it owns an editable height. each is an engineering-style dimension — a full-span
// line broken in the middle for a clickable value, with 45° ticks at the ends.
// editing the value drives the item's width/height FrmVar and re-runs the grid
// layout. The actual drawing is delegated to LinearDimension (one per axis); this
// class just maps the target's width/height + relayout onto it.
export class FrameBlockInnerDimension {
    private target: InnerDimensionTarget;

    // which axes to dimension. defaults to both; a caller can opt to a single axis
    // (e.g. a stick shows only its thickness, never its length).
    private drawWidthAxis: boolean;
    private drawHeightAxis: boolean;

    private widthDim: LinearDimension;
    private heightDim: LinearDimension;

    // when false, draw() hides both dimensions instead of rendering. lets an owner
    // toggle the dimension on/off (e.g. the "Show All Dimensions" view setting)
    // without tearing it down.
    private visible = true;

    // where each line lies across its axis, when not at DIM_INSET: a sloped cell puts
    // its height line at the tall end (see FrameBlockItem).
    private cross: {width?: number; height?: number} = {};

    constructor(target: InnerDimensionTarget, opts?: {axes?: {width?: boolean; height?: boolean}}) {
        this.target = target;
        this.drawWidthAxis = opts?.axes?.width ?? true;
        this.drawHeightAxis = opts?.axes?.height ?? true;

        const app = target.view.elev.draw;
        this.widthDim = new LinearDimension(target.dimensionContainer, app);
        this.heightDim = new LinearDimension(target.dimensionContainer, app);
    }

    // show/hide the whole dimension (lines + labels). redraws to apply.
    setVisible(visible: boolean) {
        this.visible = visible;
        this.draw();
    }

    // move an axis' line across its axis (null = back to DIM_INSET). Takes effect on
    // the next draw. True when the position changed.
    setCross(axis: "width" | "height", cross: number | null): boolean {
        const next = cross ?? undefined;
        if(this.cross[axis] === next) return false;
        this.cross[axis] = next;
        return true;
    }

    draw() {
        // in external-dimensions mode (the screen export) all measurements are drawn
        // stacked outside by ExportFrameDimensions — hide the inline per-cell/stick ones.
        if (this.target.view.elev.viewSettings.externalDimensions.value) {
            this.widthDim.hide();
            this.heightDim.hide();
            return;
        }

        if (this.visible && this.drawWidthAxis) {
            const width = this.target.width;
            this.widthDim.draw({
                axis: "horizontal",
                from: 0,
                to: width.get(),
                cross: this.cross.width ?? DIM_INSET,
                value: width.get(),
                modified: width.isModified(),
                onSet: (v) => {
                    const delta = v - width.get();
                    const wasModified = width.isModified();
                    width.userSet(v, {debug: "width-inner-dim-edit"});
                    if(this.target.onDimensionEdit?.("width", delta) === false) {
                        width.restore(v - delta, wasModified);
                    }
                    this.target.relayout();
                },
                onClear: () => {
                    width.userClearModified();
                    this.target.relayout();
                },
            });
        } else {
            this.widthDim.hide();
        }

        if (this.visible && this.drawHeightAxis) {
            const height = this.target.height;
            this.heightDim.draw({
                axis: "vertical",
                from: 0,
                to: height.get(),
                cross: this.cross.height ?? DIM_INSET,
                value: height.get(),
                modified: height.isModified(),
                onSet: (v) => {
                    const delta = v - height.get();
                    const wasModified = height.isModified();
                    height.userSet(v, {debug: "height-inner-dim-edit"});
                    if(this.target.onDimensionEdit?.("height", delta) === false) {
                        height.restore(v - delta, wasModified);
                    }
                    this.target.relayout();
                },
                onClear: () => {
                    height.userClearModified();
                    this.target.relayout();
                },
            });
        } else {
            this.heightDim.hide();
        }
    }

    // the distance from the target's top edge to the width line and from its left
    // edge to the height line. 0 for an axis that is not drawn.
    lineInsets(): {top: number; left: number} {
        const shown = this.visible && !this.target.view.elev.viewSettings.externalDimensions.value;
        return {
            top: shown && this.drawWidthAxis ? this.cross.width ?? DIM_INSET : 0,
            left: shown && this.drawHeightAxis ? this.cross.height ?? DIM_INSET : 0,
        };
    }

    // open one axis' value editor, as if the user tapped its number. Ignores an
    // axis this dimension doesn't draw.
    openEditor(axis: "width" | "height") {
        if (axis === "width") {
            if (this.drawWidthAxis) this.widthDim.openEditor();
        } else {
            if (this.drawHeightAxis) this.heightDim.openEditor();
        }
    }

    destroy() {
        this.widthDim.destroy();
        this.heightDim.destroy();
    }
}
